import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import UserStatus from './UserStatus';
import ShareButton from './ShareButton';
import { isPWA, isMobile } from '../utils/pwa';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';

export default function Navbar() {
  const { user, availableRoles } = useAuth();
  const { cart } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsPWAInstalled(isPWA());
  }, []);

  const cartItems = cart?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const isLoggedIn = !!user;

  // Determine if we should show the UserStatus component
  // UserStatus returns null when there's nothing to show (not logged in, no curious session)
  // We need to check the same conditions here
  const sessionId = localStorage.getItem('ourpickup_session');
  const hasSession = !!sessionId;
  const isCuriousStarted = localStorage.getItem('curious_started') === 'true';
  const hasCompletedModule = localStorage.getItem('curious_status') === 'active';
  const hasEmail = localStorage.getItem('curious_email') !== null;
  const isCuriousPlus = hasCompletedModule && hasEmail;
  const isCurious = (hasSession && isCuriousStarted) && !isCuriousPlus;
  const showUserStatus = isLoggedIn || isCurious || isCuriousPlus;

  return (
    <nav className="bg-green-600 text-white shadow-lg pt-safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold hover:text-green-200 transition">
            <span>🌱</span>
            <span className="hidden sm:inline">Our Pickup</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/producers" className="hover:text-green-200 transition">Producers</Link>
            <Link to="/hubs" className="hover:text-green-200 transition">Hubs</Link>
            <Link to="/products" className="hover:text-green-200 transition">Products</Link>
            <Link to="/learn" className="hover:text-green-200 transition">Learn</Link>
            <Link to="/cart" className="relative hover:text-green-200 transition">
              <ShoppingCartIcon className="h-6 w-6 text-white" />
              {cartItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItems > 9 ? '9+' : cartItems}
                </span>
              )}
            </Link>

            <ShareButton className="bg-blue-500 hover:bg-blue-600" />

            {/* UserStatus or Login/Sign Up */}
            {showUserStatus ? (
              <UserStatus user={user} availableRoles={availableRoles} />
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="text-white hover:bg-green-700 px-3 py-1.5 rounded-lg text-sm font-medium transition"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-white text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-50 transition"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Controls */}
          <div className="md:hidden flex items-center gap-2">
            {/* UserStatus or Login/Sign Up (compact) */}
            {showUserStatus ? (
              <UserStatus user={user} availableRoles={availableRoles} />
            ) : (
              <Link
                to="/login"
                className="text-white hover:bg-green-700 px-3 py-1.5 rounded-lg text-sm font-medium transition"
              >
                Login
              </Link>
            )}

            {/* Cart - show if logged in */}
            {isLoggedIn && (
              <Link to="/cart" className="relative hover:text-green-200 transition p-1">
                <ShoppingCartIcon className="h-6 w-6 text-white" />
                {cartItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                    {cartItems > 9 ? '9+' : cartItems}
                  </span>
                )}
              </Link>
            )}

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1 rounded hover:bg-green-700 transition text-2xl"
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-4 pt-2 space-y-2 border-t border-green-500/30">
            <Link
              to="/producers"
              className="block w-full text-center bg-green-700 hover:bg-green-800 px-4 py-3 rounded-lg transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Producers
            </Link>
            <Link
              to="/hubs"
              className="block w-full text-center bg-green-700 hover:bg-green-800 px-4 py-3 rounded-lg transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Hubs
            </Link>
            <Link
              to="/products"
              className="block w-full text-center bg-green-700 hover:bg-green-800 px-4 py-3 rounded-lg transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Products
            </Link>
            <Link
              to="/learn"
              className="block w-full text-center bg-green-700 hover:bg-green-800 px-4 py-3 rounded-lg transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Learn
            </Link>

            {!showUserStatus && (
              <Link
                to="/register"
                className="block w-full text-center bg-white text-green-700 hover:bg-green-50 px-4 py-3 rounded-lg font-medium transition"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign Up
              </Link>
            )}

            {isLoggedIn && (
              <Link
                to="/cart"
                className="block w-full text-center bg-green-700 hover:bg-green-800 px-4 py-3 rounded-lg transition"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                🛒 Cart {cartItems > 0 && `(${cartItems})`}
              </Link>
            )}

            <div className="pt-1">
              <ShareButton className="w-full justify-center bg-blue-500 hover:bg-blue-600" />
            </div>

            {!isPWAInstalled && isMobile() && (
              <button
                onClick={() => navigate('/install')}
                className="w-full text-center bg-green-700 hover:bg-green-800 px-4 py-3 rounded-lg text-sm transition"
              >
                📲 Install App
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
